import React, { useState, useEffect } from 'react';
import { Contact, UserPlus } from 'lucide-react';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import PipelineBoard from '@/components/pipeline/PipelineBoard';
import { useToast } from '@/hooks/errors/use-toast';
import AddProspectModal from '@/components/pipeline/AddProspectModal';
import { usePipelineData } from '@/hooks/pipeline/use-pipeline-data';
import PageActions from '@/components/ui/PageActions';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { usePipelineStages } from '@/hooks/pipeline/use-pipeline-stages';

const Pipeline = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([
    { id: 'lead', label: 'Leads', checked: false },
    { id: 'qualified', label: 'Qualified', checked: false },
    { id: 'proposal', label: 'Proposal', checked: false },
    { id: 'negotiation', label: 'Negotiation', checked: false },
    { id: 'closed', label: 'Closed', checked: false },
  ]);

  const { data: prospects, isLoading: isProspectsLoading, error } = usePipelineData();
  const { pipelineStages, isLoading: isStagesLoading } = usePipelineStages();
  const { toast } = useToast();

  // Log the pipeline data received
  useEffect(() => {
    if (prospects) {
      console.log('[Pipeline Page] Received pipeline data:', prospects);
    }
    if (error) {
      console.error('[Pipeline Page] Error fetching pipeline data:', error);
      toast({
        title: "Data Load Error",
        description: "Failed to load pipeline data. Try refreshing the page.",
        variant: "error",
      });
    }
  }, [prospects, error, toast]);

  // Transform prospects into columns
  const columns = React.useMemo(() => {
    console.log('[Pipeline Page] Transforming prospects to columns');
    
    if (!prospects) {
      console.log('[Pipeline Page] No prospects data, using empty columns');
      return pipelineStages.map(stage => ({
        id: stage.id,
        title: stage.title,
        prospects: []
      }));
    }

    const columnData = pipelineStages.map(stage => ({
      id: stage.id,
      title: stage.title,
      prospects: prospects.filter(p => p.stage === stage.id)
    }));
    
    console.log('[Pipeline Page] Columns created:', columnData);
    return columnData;
  }, [prospects, pipelineStages]);

  const handleFilterChange = (updatedOptions: FilterOption[]) => {
    setFilterOptions(updatedOptions);
  };

  if (isProspectsLoading || isStagesLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center h-[60vh]">
          Loading prospects...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <DashboardHeader 
        title="Prospects"
        description="Manage and track your sales prospects."
        icon={<Contact size={24} />}
        primaryAction={{
          label: "Create",
          icon: UserPlus,
          onClick: () => setIsAddModalOpen(true)
        }}
      />
      
      <PageActions
        filterOptions={{
          options: filterOptions,
          onFilterChange: handleFilterChange,
          label: "Filters"
        }}
      />
      
      <div className="bg-white-800/50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-lg overflow-hidden">
        <PipelineBoard initialColumns={columns as any} />
      </div>
      
      <AddProspectModal 
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </Container>
  );
};

export default Pipeline; 